import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { TOGGLE_NAMES } from 'platform/utilities/feature-toggles';
import { isVAPatient } from '../../../selectors';
import { ANALYTICS_FIELD_MAP, FIELD_NAMES } from '../constants';
import {
  selectAddressValidationType,
  selectCurrentlyOpenEditModal,
  selectEditViewData,
  selectVAPContactInfoField,
  selectVAPServiceTransaction,
} from '../selectors';
import { isFieldEmpty } from '../util';
import getProfileInfoFieldAttributes from '../util/getProfileInfoFieldAttributes';
import {
  buildCustomUiSchema,
  deriveModalFlags,
  deriveTransactionError,
  selectFieldData,
  shouldShowErrorAlert,
  shouldShowUpdateSuccessAlert,
} from '../util/profile-information-field/fieldControllerSelectors';

/**
 * Everything the field controller used to receive from connect(), as a hook.
 *
 * PORT NOTE: mapStateToProps was a single function, but it cannot become a
 * single useSelector. selectVAPServiceTransaction builds a fresh wrapper object
 * on every call, so one selector returning the whole shape would fail its
 * equality check on every dispatched action and re-render the field each time.
 * connect() absorbed that with its own shallow comparison; here the store reads
 * are split, and the two that build fresh objects pass shallowEqual.
 */
export const useProfileInformationFieldData = (
  fieldName,
  { title, allowInternationalPhones = false } = {},
) => {
  const internationalPhonesEnabled = useSelector(
    state => state.featureToggles?.profileInternationalPhoneNumbers || false,
  );
  const useUnicodeAddressValidation = useSelector(
    state =>
      state.featureToggles?.profileInternationalAddressValidationEnabled ||
      false,
  );
  const useVapSvcShowErrors = useSelector(
    state => state.featureToggles?.[TOGGLE_NAMES.cveVapSvcShowErrors] || false,
  );
  const useProfileServiceRefactor = useSelector(
    state =>
      state.featureToggles?.[TOGGLE_NAMES.cveProfileServiceRefactor] || false,
  );

  const enableInternationalPhones =
    allowInternationalPhones && internationalPhonesEnabled;

  // shallowEqual is required here: selectVAPServiceTransaction builds a fresh
  // object on every call.
  const { transaction, transactionRequest } = useSelector(
    state => selectVAPServiceTransaction(state, fieldName),
    shallowEqual,
  );

  const data = useSelector(
    state => selectFieldData(state, fieldName),
    shallowEqual,
  );

  const activeEditView = useSelector(selectCurrentlyOpenEditModal);
  const addressValidationType = useSelector(selectAddressValidationType);
  const editViewData = useSelector(selectEditViewData);
  const hasUnsavedEdits = useSelector(
    state => state.vapService?.hasUnsavedEdits,
  );
  const isEnrolledInVAHealthCare = useSelector(isVAPatient);

  const showUpdateSuccessAlert = useSelector(state =>
    shouldShowUpdateSuccessAlert(state, fieldName),
  );
  const showErrorAlert = useSelector(state =>
    shouldShowErrorAlert(state, fieldName),
  );
  const showCopyAddressModal = useSelector(
    state =>
      fieldName === FIELD_NAMES.MAILING_ADDRESS &&
      !!state.vapService?.copyAddressModal,
  );

  const email = useSelector(state => selectVAPContactInfoField(state, 'email'));
  const mailingAddress = useSelector(state =>
    selectVAPContactInfoField(state, 'mailingAddress'),
  );
  const mobilePhone = useSelector(state =>
    selectVAPContactInfoField(state, 'mobilePhone'),
  );
  const homePhone = useSelector(state =>
    selectVAPContactInfoField(state, 'homePhone'),
  );
  const workPhone = useSelector(state =>
    selectVAPContactInfoField(state, 'workPhone'),
  );

  // Memoized for cost, not identity: this deep-clones the phone and address
  // schemas, and the edit view dispatches a store update on every keystroke,
  // which re-runs this hook each time.
  const {
    apiRoute,
    convertCleanDataToPayload,
    uiSchema,
    formSchema,
    title: defaultTitle,
  } = useMemo(
    () =>
      getProfileInfoFieldAttributes(fieldName, {
        allowInternationalPhones: enableInternationalPhones,
        useUnicodeAddressValidation,
      }),
    [fieldName, enableInternationalPhones, useUnicodeAddressValidation],
  );

  const customUiSchema = buildCustomUiSchema(uiSchema, title);

  const modalFlags = deriveModalFlags({
    fieldName,
    activeEditView,
    addressValidationType,
    hasUnsavedEdits,
  });

  return {
    ...modalFlags,
    analyticsSectionName: ANALYTICS_FIELD_MAP[fieldName],
    apiRoute,
    convertCleanDataToPayload,
    data,
    editViewData,
    email,
    fieldName,
    formSchema,
    hasUnsavedEdits,
    homePhone,
    isEmpty: isFieldEmpty(data, fieldName),
    isEnrolledInVAHealthCare,
    mailingAddress,
    mobilePhone,
    showCopyAddressModal,
    showErrorAlert,
    showUpdateSuccessAlert,
    title: title || defaultTitle, // Use custom title if provided
    transaction,
    transactionError: deriveTransactionError(transaction, transactionRequest),
    transactionRequest,
    uiSchema: customUiSchema,
    useProfileServiceRefactor,
    useVapSvcShowErrors,
    workPhone,
  };
};
